import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flag, ChevronLeft, ChevronRight, Clock, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import SEO from "../components/SEO";
import { Emblem } from "../components/Emblem";

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function TestRun() {
  const { testId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [test, setTest] = useState(state?.test || null);
  const [loading] = useState(!state?.test);
  const [err, setErr] = useState("");
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!test) setErr("Sesión de test perdida. Vuelve a configurarlo.");
  }, [test]);

  useEffect(() => {
    if (!test) return;
    const i = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(i);
  }, [test]);

  const totalSeconds = test?.duration_minutes ? test.duration_minutes * 60 : null;
  const remaining = totalSeconds ? Math.max(0, totalSeconds - elapsed) : null;
  const isCritical = remaining !== null && remaining < 60;

  const questions = test?.questions || [];
  const q = questions[current];
  const answeredCount = Object.keys(answers).length;
  const progress = useMemo(() => (questions.length ? (current + 1) / questions.length * 100 : 0), [current, questions.length]);

  const selectAnswer = (idx) => { if (q) setAnswers((a) => ({ ...a, [q.id]: idx })); };

  const submit = async () => {
    try {
      const payload = {
        answers: questions.map((qq) => ({ question_id: qq.id, selected_index: answers[qq.id] ?? null })),
        elapsed_seconds: elapsed,
      };
      const { data } = await api.post(`/tests/${testId}/submit`, payload);
      navigate(`/results/${data.id}`, { state: { result: data } });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => {
    if (remaining === 0 && totalSeconds && !confirmOpen) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  if (loading) return <div className="min-h-screen bg-ink flex items-center justify-center text-slate-500">Cargando test...</div>;
  if (err && !test) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <Alert variant="destructive" className="border-red-900/50 bg-red-950/40 text-red-200"><AlertDescription>{err}</AlertDescription></Alert>
          <Button onClick={() => navigate("/tests")} className="mt-4 bg-gcgreen hover:bg-gcgreen-light">Volver</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink relative">
      <SEO title="Test en curso" path={`/tests/run/${testId}`} />
      <div className="fixed inset-0 bg-aurora pointer-events-none opacity-50" />

      {/* Sticky header bar */}
      <header className="sticky top-0 z-30 glass-header">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Emblem className="h-8 w-8" />
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono hidden sm:block">Test en curso</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400 font-mono">
              <span className="text-white font-bold">{current + 1}</span>
              <span className="text-slate-600">/</span>
              <span>{questions.length}</span>
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono text-sm font-bold border transition-colors ${
              isCritical ? "bg-red-950/50 text-red-400 border-red-700/30 animate-pulse" : "bg-white/5 text-white border-white/10"
            }`} data-testid="test-timer">
              <Clock className="h-3.5 w-3.5" />
              {remaining !== null ? formatTime(remaining) : formatTime(elapsed)}
            </div>
            <Button
              variant="outline" size="sm" onClick={() => setConfirmOpen(true)}
              className="bg-gcgold/10 border-gcgold/30 text-gcgold hover:bg-gcgold hover:text-ink rounded-xl"
              data-testid="test-finish-button"
            >
              Finalizar
            </Button>
          </div>
        </div>
        <div className="h-1 bg-white/5">
          <div className="h-full bg-gradient-to-r from-gcgreen to-gcgold transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <main className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Question card */}
        <div className="bg-ink-800 border border-white/5 rounded-3xl p-6 sm:p-10">
          <div className="flex items-center justify-between mb-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold font-mono">Pregunta {current + 1}</div>
            <button
              type="button"
              onClick={() => setFlags((f) => ({ ...f, [q.id]: !f[q.id] }))}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl border transition-colors ${
                flags[q?.id] ? "bg-gcgold/10 border-gcgold/40 text-gcgold" : "border-white/10 text-slate-400 hover:border-white/20"
              }`}
              data-testid="flag-question-button"
            >
              <Flag className="h-3 w-3" /> {flags[q?.id] ? "Marcada" : "Marcar"}
            </button>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight" data-testid="question-text">
            {q?.question}
          </h2>

          <div className="mt-8 space-y-2.5" data-testid="options-list">
            {q?.options?.map((opt, idx) => {
              const sel = answers[q.id] === idx;
              const letter = String.fromCharCode(65 + idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => selectAnswer(idx)}
                  className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border transition-all ${
                    sel
                      ? "border-gcgold/50 bg-gcgold/10 shadow-[0_0_30px_-8px_rgba(212,175,55,0.5)]"
                      : "border-white/5 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"
                  }`}
                  data-testid={`option-${idx}`}
                >
                  <span className={`h-9 w-9 flex items-center justify-center rounded-lg font-mono text-sm font-bold flex-shrink-0 ${
                    sel ? "bg-gcgold text-ink" : "bg-white/5 text-slate-400 border border-white/10"
                  }`}>{letter}</span>
                  <span className="text-sm sm:text-base text-slate-200 flex-1">{opt}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-10 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrent((c) => Math.max(0, c - 1))}
              disabled={current === 0}
              className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white rounded-xl"
              data-testid="prev-question"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
            </Button>
            {current < questions.length - 1 ? (
              <Button
                onClick={() => setCurrent((c) => Math.min(questions.length - 1, c + 1))}
                className="bg-gcgreen hover:bg-gcgreen-light text-white rounded-xl shadow-[0_0_30px_-8px_rgba(10,92,54,0.6)]"
                data-testid="next-question"
              >
                Siguiente <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={() => setConfirmOpen(true)}
                className="bg-gcgold hover:bg-gcgold-light text-ink font-bold rounded-xl shadow-[0_0_30px_-8px_rgba(212,175,55,0.6)]"
                data-testid="submit-test-button"
              >
                Finalizar test
              </Button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-ink-800 border border-white/5 rounded-2xl p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-3 font-mono">Resumen</div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-400">Respondidas</span>
              <span className="font-mono font-bold text-gcgold">{answeredCount} / {questions.length}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gcgold rounded-full transition-all" style={{ width: `${(answeredCount / questions.length) * 100}%` }} />
            </div>

            <div className="mt-5 grid grid-cols-5 gap-1.5" data-testid="question-nav">
              {questions.map((qq, idx) => {
                const ans = answers[qq.id] !== undefined;
                const fl = flags[qq.id];
                const isCur = idx === current;
                return (
                  <button
                    key={qq.id}
                    onClick={() => setCurrent(idx)}
                    className={`h-9 text-xs font-mono font-bold rounded-lg border transition-all relative ${
                      isCur ? "border-gcgold bg-gcgold text-ink"
                      : ans ? "border-gcgreen/40 bg-gcgreen/10 text-gcgreen-glow"
                      : "border-white/5 text-slate-500 hover:border-white/15"
                    }`}
                    data-testid={`nav-q-${idx + 1}`}
                  >
                    {idx + 1}
                    {fl && <span className="absolute -top-1 -right-1 h-1.5 w-1.5 bg-rose-500 rounded-full" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-4 border-t border-white/5 space-y-1.5 text-[10px] text-slate-500 font-mono uppercase tracking-wider">
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm bg-gcgold" /> Actual</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm border border-gcgreen/50 bg-gcgreen/10" /> Respondida</div>
              <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm border border-white/10" /> Sin responder</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Marcada</div>
            </div>
          </div>

        </aside>
      </main>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-ink-800 border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="h-4 w-4 text-gcgold" />
              ¿Finalizar el test?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Has respondido {answeredCount} de {questions.length} preguntas.
              Las preguntas sin responder no penalizan, pero tampoco suman.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white" data-testid="cancel-submit">Seguir</AlertDialogCancel>
            <AlertDialogAction onClick={submit} className="bg-gcgold hover:bg-gcgold-light text-ink font-bold" data-testid="confirm-submit">
              Finalizar y ver resultado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
